import { TodoTree, TodoItem, DEFAULT_TODO_ITEM, ROOT_NODE_NAME } from '../types/todo';
// Stage 3.3: Nested format generators
import { NestedTodoTree, NestedTodoNode, NestedTodoItem } from '../types/todo';
import { generateUUID } from './uuid';
import { FILES, ROOT_NODE } from './constants';

export function createTodoItem(
  name: string,
  overrides: Partial<TodoItem> = {}
): TodoItem {
  return {
    ...DEFAULT_TODO_ITEM,
    name,
    ...overrides,
  };
}

/**
 * Generates a comprehensive sample todo tree in flat format.
 * This is a wrapper that generates the nested format and converts it to flat.
 * The nested format is the single source of truth for sample data.
 */
export function generateSampleTodoTree(): TodoTree {
  return nestedToFlatTreeForInternalUse(generateSampleNestedTodoTree());
}

export function createMinimalTodoTree(): TodoTree {
  const rootId = ROOT_NODE.UUID;
  const taskId = generateUUID();

  return {
    [rootId]: createTodoItem(ROOT_NODE_NAME, {
      children: [taskId],
    }),
    [taskId]: createTodoItem('Sample Task', {
      children: [],
      priority: 'medium',
    }),
  };
}

export function exportTodoTreeAsJson(tree: TodoTree): string {
  return JSON.stringify(tree, null, FILES.JSON_INDENT_SPACES);
}

export function createNestedTodoItem(
  name: string,
  overrides: Partial<NestedTodoItem> = {}
): NestedTodoItem {
  return {
    name,
    children: [],
    archived: false,
    done: false,
    blocked: false,
    priority: 'medium',
    visibility: 'show-active',
    description: '',
    highlighted: false,
    ...overrides,
  };
}

export function generateSampleNestedTodoTree(): NestedTodoTree {
  // Generate UUIDs for all nodes to match flat version structure
  const rootId = ROOT_NODE.UUID;

  // Level 1: Main Projects
  const softwareDevId = generateUUID();
  const marketingId = generateUUID();
  const researchId = generateUUID();
  const adminId = generateUUID();

  // Software Development - Phase 1: Planning
  const phase1Id = generateUUID();
  const requirementsId = generateUUID();
  const techDesignId = generateUUID();
  const resourcePlanId = generateUUID();

  // Requirements subtasks
  const stakeholderInterviewsId = generateUUID();
  const userResearchId = generateUUID();
  const competitiveAnalysisId = generateUUID();

  // Tech Design subtasks
  const architectureId = generateUUID();
  const databaseSchemaId = generateUUID();
  const apiDesignId = generateUUID();

  // Software Development - Phase 2: Development
  const phase2Id = generateUUID();
  const frontendId = generateUUID();
  const backendId = generateUUID();
  const devopsId = generateUUID();

  // Frontend subtasks
  const componentLibId = generateUUID();
  const stateManagementId = generateUUID();
  const routingId = generateUUID();
  const frontendTestingId = generateUUID();

  // Component Library sub-subtasks
  const buttonComponentsId = generateUUID();
  const formComponentsId = generateUUID();
  const navComponentsId = generateUUID();
  const dataDisplayId = generateUUID();

  // State Management sub-subtasks
  const setupReduxId = generateUUID();
  const createSlicesId = generateUUID();
  const middlewareId = generateUUID();

  // Backend subtasks
  const apiDevId = generateUUID();
  const databaseId = generateUUID();
  const securityId = generateUUID();

  // API Development sub-subtasks
  const authEndpointsId = generateUUID();
  const userManagementId = generateUUID();
  const dataEndpointsId = generateUUID();
  const fileUploadId = generateUUID();

  // Database sub-subtasks
  const setupPostgresId = generateUUID();
  const migrationsId = generateUUID();
  const seedingId = generateUUID();

  // DevOps subtasks
  const cicdId = generateUUID();
  const dockerId = generateUUID();
  const monitoringId = generateUUID();
  const loggingId = generateUUID();

  // Software Development - Phase 3: Testing
  const phase3Id = generateUUID();
  const unitTestsId = generateUUID();
  const integrationTestsId = generateUUID();
  const e2eTestsId = generateUUID();
  const perfTestingId = generateUUID();

  // Software Development - Phase 4: Deployment
  const phase4Id = generateUUID();
  const stagingId = generateUUID();
  const prodDeployId = generateUUID();
  const deployMonitoringId = generateUUID();

  // Marketing Campaign
  const contentCreationId = generateUUID();
  const blogPostsId = generateUUID();
  const socialMediaId = generateUUID();
  const videoContentId = generateUUID();
  const seoId = generateUUID();
  const emailMarketingId = generateUUID();

  // Research Projects
  const aiStudyId = generateUUID();
  const literatureReviewId = generateUUID();
  const prototypeDevId = generateUUID();
  const perfAnalysisId = generateUUID();
  const uxStudyId = generateUUID();
  const marketAnalysisId = generateUUID();

  // Administrative Tasks
  const budgetPlanningId = generateUUID();
  const teamMeetingsId = generateUUID();
  const documentationId = generateUUID();

  return {
    [rootId]: createNestedTodoItem(ROOT_NODE_NAME, {
      priority: 'high',
      children: [
        // ===== SOFTWARE DEVELOPMENT PROJECT =====
        {
          [softwareDevId]: createNestedTodoItem('Software Development Project', {
            priority: 'critical',
            visibility: 'show-all',
            description: 'Complete product development lifecycle from planning through deployment.\n\nThis project demonstrates all features:\n- **Deep nesting** with various priorities\n- *States* and visibility controls\n- [React docs](https://react.dev) for reference',
            children: [
              // Phase 1: Planning (COMPLETED)
              {
                [phase1Id]: createNestedTodoItem('Phase 1: Planning', {
                  priority: 'high',
                  done: true,
                  description: 'Initial project planning phase including requirements gathering, technical design, and resource allocation.',
                  children: [
                    {
                      [requirementsId]: createNestedTodoItem('Requirements Gathering', {
                        priority: 'high',
                        done: true,
                        description: 'Comprehensive requirements collection through stakeholder engagement and market research.',
                        children: [
                          {
                            [stakeholderInterviewsId]: createNestedTodoItem('Stakeholder Interviews', {
                              priority: 'medium',
                              done: true,
                              description: 'Conduct interviews with key stakeholders to understand business requirements and success criteria.',
                            })
                          },
                          {
                            [userResearchId]: createNestedTodoItem('User Research', {
                              priority: 'medium',
                              done: true,
                              description: 'Perform user surveys and usability studies to identify pain points and desired features.',
                            })
                          },
                          {
                            [competitiveAnalysisId]: createNestedTodoItem('Competitive Analysis', {
                              priority: 'low',
                              done: true,
                              description: 'Analyze competitor products to identify market gaps and differentiation opportunities.',
                            })
                          }
                        ]
                      })
                    },
                    {
                      [techDesignId]: createNestedTodoItem('Technical Design', {
                        priority: 'high',
                        done: true,
                        description: 'Detailed technical architecture and design specifications for the system.',
                        children: [
                          {
                            [architectureId]: createNestedTodoItem('Architecture Design', {
                              priority: 'critical',
                              done: true,
                              description: 'Design microservices architecture with event-driven communication patterns and scalability considerations.',
                            })
                          },
                          {
                            [databaseSchemaId]: createNestedTodoItem('Database Schema', {
                              priority: 'high',
                              done: true,
                              description: 'Design normalized database schema with proper indexing and relationship management.',
                            })
                          },
                          {
                            [apiDesignId]: createNestedTodoItem('API Design', {
                              priority: 'medium',
                              done: true,
                              description: 'Define RESTful API endpoints following OpenAPI specification with versioning strategy.',
                            })
                          }
                        ]
                      })
                    },
                    {
                      [resourcePlanId]: createNestedTodoItem('Resource Planning', {
                        priority: 'medium',
                        done: true,
                        description: 'Allocate team members, budget, and timeline for project execution.',
                      })
                    }
                  ]
                })
              },
              // Phase 2: Development (IN PROGRESS)
              {
                [phase2Id]: createNestedTodoItem('Phase 2: Development', {
                  priority: 'critical',
                  description: 'Active development phase with parallel frontend, backend, and DevOps workstreams.',
                  children: [
                    {
                      [frontendId]: createNestedTodoItem('Frontend Development', {
                        priority: 'high',
                        description: 'Build responsive, accessible user interface with modern React patterns.',
                        children: [
                          {
                            [componentLibId]: createNestedTodoItem('Component Library', {
                              priority: 'high',
                              highlighted: true,
                              description: 'Reusable component library with consistent styling and behavior. HIGHLIGHTED as high priority deliverable.',
                              children: [
                                {
                                  [buttonComponentsId]: createNestedTodoItem('Button Components', {
                                    priority: 'medium',
                                    done: true,
                                    description: 'Primary, secondary, and icon buttons with loading and disabled states.',
                                  })
                                },
                                {
                                  [formComponentsId]: createNestedTodoItem('Form Components', {
                                    priority: 'medium',
                                    description: 'Text inputs, selects, checkboxes, radio buttons with validation and error handling.',
                                  })
                                },
                                {
                                  [navComponentsId]: createNestedTodoItem('Navigation Components', {
                                    priority: 'medium',
                                    blocked: true,
                                    description: 'Header, sidebar, breadcrumbs, and mobile menu components. BLOCKED pending design approval.',
                                  })
                                },
                                {
                                  [dataDisplayId]: createNestedTodoItem('Data Display Components', {
                                    priority: 'low',
                                    description: 'Tables, lists, cards, and data visualization components.',
                                  })
                                }
                              ]
                            })
                          },
                          {
                            [stateManagementId]: createNestedTodoItem('State Management', {
                              priority: 'critical',
                              description: 'Centralized state management with Redux Toolkit for predictable state updates.',
                              children: [
                                {
                                  [setupReduxId]: createNestedTodoItem('Setup Redux Store', {
                                    priority: 'high',
                                    done: true,
                                    description: 'Configure Redux store with TypeScript support and DevTools integration.',
                                  })
                                },
                                {
                                  [createSlicesId]: createNestedTodoItem('Create Feature Slices', {
                                    priority: 'medium',
                                    description: 'Define reducers and actions for auth, user, todos, and settings features.',
                                  })
                                },
                                {
                                  [middlewareId]: createNestedTodoItem('Middleware Configuration', {
                                    priority: 'low',
                                    description: 'Setup Redux middleware for async operations, logging, and error tracking.',
                                  })
                                }
                              ]
                            })
                          },
                          {
                            [routingId]: createNestedTodoItem('Routing Setup', {
                              priority: 'high',
                              description: 'Configure React Router with protected routes, lazy loading, and navigation guards.\n\n> **Note:** Important milestone for the project.',
                            })
                          },
                          {
                            [frontendTestingId]: createNestedTodoItem('Frontend Testing', {
                              priority: 'medium',
                              blocked: true,
                              description: 'Component tests with React Testing Library and E2E tests with Playwright. BLOCKED pending test environment setup.',
                            })
                          }
                        ]
                      })
                    },
                    {
                      [backendId]: createNestedTodoItem('Backend Development', {
                        priority: 'high',
                        description: 'Scalable backend services with proper error handling and logging.',
                        children: [
                          {
                            [apiDevId]: createNestedTodoItem('API Development', {
                              priority: 'critical',
                              description: 'RESTful API endpoints with validation, rate limiting, and documentation.',
                              children: [
                                {
                                  [authEndpointsId]: createNestedTodoItem('Authentication Endpoints', {
                                    priority: 'critical',
                                    highlighted: true,
                                    description: 'Secure auth endpoints with JWT tokens, refresh tokens, and OAuth integration.\n\n**HIGHLIGHTED** as critical security feature.\n\n```typescript\nconst auth = jwt.verify(token, secret);\n```',
                                  })
                                },
                                {
                                  [userManagementId]: createNestedTodoItem('User Management', {
                                    priority: 'high',
                                    description: 'User CRUD operations, profile management, and role-based access control.',
                                  })
                                },
                                {
                                  [dataEndpointsId]: createNestedTodoItem('Data Endpoints', {
                                    priority: 'medium',
                                    description: 'Core business logic endpoints with pagination, filtering, and sorting.',
                                  })
                                },
                                {
                                  [fileUploadId]: createNestedTodoItem('File Upload System', {
                                    priority: 'low',
                                    description: 'Multi-part file uploads with progress tracking and cloud storage integration.',
                                  })
                                }
                              ]
                            })
                          },
                          {
                            [databaseId]: createNestedTodoItem('Database Implementation', {
                              priority: 'high',
                              description: 'PostgreSQL database with migrations, seeding, and backup strategies.',
                              children: [
                                {
                                  [setupPostgresId]: createNestedTodoItem('Setup PostgreSQL', {
                                    priority: 'high',
                                    done: true,
                                    description: 'Install and configure PostgreSQL with connection pooling and SSL.',
                                  })
                                },
                                {
                                  [migrationsId]: createNestedTodoItem('Database Migrations', {
                                    priority: 'medium',
                                    description: 'Version-controlled schema migrations with rollback capabilities.',
                                  })
                                },
                                {
                                  [seedingId]: createNestedTodoItem('Data Seeding', {
                                    priority: 'low',
                                    description: 'Development and testing data fixtures for consistent environments.',
                                  })
                                }
                              ]
                            })
                          },
                          {
                            [securityId]: createNestedTodoItem('Security Implementation', {
                              priority: 'critical',
                              blocked: true,
                              description: 'OWASP Top 10 protection, input sanitization, CSRF tokens, and security headers. BLOCKED pending security audit.',
                            })
                          }
                        ]
                      })
                    },
                    {
                      [devopsId]: createNestedTodoItem('DevOps & Infrastructure', {
                        priority: 'medium',
                        description: 'Automated deployment pipeline and production infrastructure.',
                        children: [
                          {
                            [cicdId]: createNestedTodoItem('CI/CD Pipeline', {
                              priority: 'high',
                              description: 'GitHub Actions workflow for automated testing, building, and deployment.\n\nSteps:\n1. Setup workflow YAML\n2. Add test runners\n3. Configure deployment triggers',
                            })
                          },
                          {
                            [dockerId]: createNestedTodoItem('Docker Configuration', {
                              priority: 'medium',
                              done: true,
                              description: 'Multi-stage Docker builds with optimized images and docker-compose for local development.',
                            })
                          },
                          {
                            [monitoringId]: createNestedTodoItem('Application Monitoring', {
                              priority: 'low',
                              description: 'Setup monitoring with metrics, alerts, and performance dashboards.',
                            })
                          },
                          {
                            [loggingId]: createNestedTodoItem('Centralized Logging', {
                              priority: 'low',
                              description: 'Structured logging with log aggregation and search capabilities.',
                            })
                          }
                        ]
                      })
                    }
                  ]
                })
              },
              // Phase 3: Testing (HIDDEN)
              {
                [phase3Id]: createNestedTodoItem('Phase 3: Testing', {
                  priority: 'high',
                  visibility: 'hide-all',
                  description: 'Comprehensive testing strategy across all layers. VISIBILITY: hide-all to demonstrate feature.',
                  children: [
                    {
                      [unitTestsId]: createNestedTodoItem('Unit Tests', {
                        priority: 'high',
                        description: 'Component and function-level tests with >80% code coverage.',
                      })
                    },
                    {
                      [integrationTestsId]: createNestedTodoItem('Integration Tests', {
                        priority: 'medium',
                        description: 'API integration tests with database and external service mocks.',
                      })
                    },
                    {
                      [e2eTestsId]: createNestedTodoItem('E2E Tests', {
                        priority: 'medium',
                        description: 'End-to-end user journey tests covering critical workflows.',
                      })
                    },
                    {
                      [perfTestingId]: createNestedTodoItem('Performance Testing', {
                        priority: 'low',
                        description: 'Load testing, stress testing, and performance benchmarking.',
                      })
                    }
                  ]
                })
              },
              // Phase 4: Deployment
              {
                [phase4Id]: createNestedTodoItem('Phase 4: Deployment', {
                  priority: 'medium',
                  description: 'Production deployment and go-live activities.',
                  children: [
                    {
                      [stagingId]: createNestedTodoItem('Staging Environment', {
                        priority: 'high',
                        description: 'Deploy to staging for UAT and final validation before production.',
                      })
                    },
                    {
                      [prodDeployId]: createNestedTodoItem('Production Deployment', {
                        priority: 'critical',
                        description: 'Blue-green deployment to production with rollback plan.',
                      })
                    },
                    {
                      [deployMonitoringId]: createNestedTodoItem('Post-Deploy Monitoring', {
                        priority: 'medium',
                        description: 'Monitor application health, performance, and user feedback post-launch.',
                      })
                    }
                  ]
                })
              }
            ]
          })
        },
        // ===== MARKETING CAMPAIGN =====
        {
          [marketingId]: createNestedTodoItem('Marketing Campaign', {
            priority: 'medium',
            description: 'Product marketing and user acquisition initiatives.',
            children: [
              {
                [contentCreationId]: createNestedTodoItem('Content Creation', {
                  priority: 'high',
                  description: 'Create engaging content across multiple channels.',
                  children: [
                    {
                      [blogPostsId]: createNestedTodoItem('Blog Posts', {
                        priority: 'medium',
                        description: 'Technical blog posts and case studies showcasing product features.\n\n- [ ] Draft outline\n- [ ] Write first draft\n- [ ] Review and publish',
                      })
                    },
                    {
                      [socialMediaId]: createNestedTodoItem('Social Media Content', {
                        priority: 'low',
                        description: 'Daily posts, stories, and engagement on Twitter, LinkedIn, and Reddit.',
                      })
                    },
                    {
                      [videoContentId]: createNestedTodoItem('Video Content', {
                        priority: 'low',
                        description: 'Tutorial videos, product demos, and customer testimonials.',
                      })
                    }
                  ]
                })
              },
              {
                [seoId]: createNestedTodoItem('SEO Optimization', {
                  priority: 'medium',
                  done: true,
                  archived: true,
                  description: 'Search engine optimization for organic traffic growth. COMPLETED and ARCHIVED.',
                })
              },
              {
                [emailMarketingId]: createNestedTodoItem('Email Marketing', {
                  priority: 'low',
                  archived: true,
                  description: 'Newsletter campaigns and drip sequences. ARCHIVED - switched to other channels.',
                })
              }
            ]
          })
        },
        // ===== RESEARCH PROJECTS =====
        {
          [researchId]: createNestedTodoItem('Research Projects', {
            priority: 'low',
            visibility: 'show-active',
            description: 'Exploratory research and innovation projects. VISIBILITY: show-active (default).',
            children: [
              {
                [aiStudyId]: createNestedTodoItem('AI Integration Study', {
                  priority: 'medium',
                  description: 'Explore AI/ML integration opportunities for smart task suggestions.',
                  children: [
                    {
                      [literatureReviewId]: createNestedTodoItem('Literature Review', {
                        priority: 'medium',
                        done: true,
                        description: 'Review academic papers and industry best practices for AI task management.',
                      })
                    },
                    {
                      [prototypeDevId]: createNestedTodoItem('Prototype Development', {
                        priority: 'high',
                        description: 'Build proof-of-concept for AI-powered task prioritization.',
                      })
                    },
                    {
                      [perfAnalysisId]: createNestedTodoItem('Performance Analysis', {
                        priority: 'low',
                        description: 'Evaluate accuracy, latency, and resource requirements of AI model.',
                      })
                    }
                  ]
                })
              },
              {
                [uxStudyId]: createNestedTodoItem('User Experience Study', {
                  priority: 'low',
                  done: true,
                  archived: true,
                  description: 'Comprehensive UX research with usability testing. COMPLETED and ARCHIVED.',
                })
              },
              {
                [marketAnalysisId]: createNestedTodoItem('Market Analysis', {
                  priority: 'low',
                  description: 'Ongoing market trends and competitor monitoring.',
                })
              }
            ]
          })
        },
        // ===== ADMINISTRATIVE TASKS =====
        {
          [adminId]: createNestedTodoItem('Administrative Tasks', {
            priority: 'low',
            archived: true,
            description: 'Routine administrative tasks. Entire category ARCHIVED.',
            children: [
              {
                [budgetPlanningId]: createNestedTodoItem('Budget Planning', {
                  priority: 'medium',
                  done: true,
                  archived: true,
                  description: 'Q4 budget allocation and financial planning.',
                })
              },
              {
                [teamMeetingsId]: createNestedTodoItem('Team Meetings', {
                  priority: 'low',
                  done: true,
                  archived: true,
                  description: 'Weekly standup and monthly retrospective meetings.',
                })
              },
              {
                [documentationId]: createNestedTodoItem('Documentation Updates', {
                  priority: 'low',
                  done: true,
                  archived: true,
                  description: 'Update internal wikis and process documentation.',
                })
              }
            ]
          })
        }
      ]
    })
  };
}

// Utility function to convert nested format to flat format for internal use
export function nestedToFlatTreeForInternalUse(nestedTree: NestedTodoTree): TodoTree {
  const flatTree: TodoTree = {};

  function convertNestedToFlat(nestedNode: NestedTodoNode): string {
    const [nodeId, nodeItem] = Object.entries(nestedNode)[0];

    // Extract child IDs by recursively processing children
    const childIds: string[] = [];
    const children = nodeItem.children || [];
    for (const childNode of children) {
      const childId = convertNestedToFlat(childNode);
      childIds.push(childId);
    }

    // Migration logic: Convert old visibility properties to new format
    let visibility = nodeItem.visibility ?? DEFAULT_TODO_ITEM.visibility;
    const anyItem = nodeItem as any; // Temporary type cast for migration
    if (anyItem.hideAllChildren === true) {
      visibility = 'hide-all';
    } else if (anyItem.showAllChildren === true) {
      visibility = 'show-all';
    }

    // Add this item to the flat tree with defaults for missing fields
    flatTree[nodeId] = {
      name: nodeItem.name,
      children: childIds,
      archived: nodeItem.archived ?? DEFAULT_TODO_ITEM.archived,
      done: nodeItem.done ?? DEFAULT_TODO_ITEM.done,
      blocked: nodeItem.blocked ?? DEFAULT_TODO_ITEM.blocked,
      priority: nodeItem.priority ?? DEFAULT_TODO_ITEM.priority,
      visibility,
      description: nodeItem.description ?? DEFAULT_TODO_ITEM.description,
      highlighted: nodeItem.highlighted ?? DEFAULT_TODO_ITEM.highlighted,
      timeEstimate: nodeItem.timeEstimate ?? DEFAULT_TODO_ITEM.timeEstimate,
    };

    return nodeId;
  }

  convertNestedToFlat(nestedTree);
  return flatTree;
}